import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const JSInheritance = () => {
    const classInheritanceCode = `// --- Base Component ---
function Component(id) {
    this.id = id;
}
Component.prototype.render = function() {
    console.log(\`Rendering [\${this.id}]\`);
};

// --- Child Component (Constructor Stealing) ---
function Button(id, label) {
    // 1. "Steal" the parent constructor's properties
    Component.call(this, id); 
    this.label = label;
}

// 2. Link the Prototype Chain
Button.prototype = Object.create(Component.prototype);
Button.prototype.constructor = Button;

// 3. Extension (Method Override)
Button.prototype.render = function() {
    Component.prototype.render.call(this); // Super-like call
    console.log(\`Label: \${this.label}\`);
};`;

    const prototypeChainCode = `// --- Pure Prototypal Chain ---
const machine = {
    powerOn() { this.active = true; }
};

const robot = Object.create(machine);
robot.task = 'Cleanup';

const vacuum = Object.create(robot);

// JavaScript digs through the chain:
// vacuum.active? -&gt; No
// robot.active?  -&gt; No
// machine.active? -&gt; Found!
vacuum.powerOn(); 
console.log(vacuum.active); // true`;

    const compositionPatternCode = `// --- Composition (The "Has-A" Strategy) ---
const canFly = (state) => ({
    fly: () => console.log(\`\${state.name} is soaring!\`)
});

const canAttack = (state) => ({
    attack: () => console.log(\`\${state.name} strikes for \${state.dmg}!\`)
});

const createDragon = (name) => {
    const state = { name, dmg: 50 };
    // Combine behaviors into a new object
    return Object.assign(state, canFly(state), canAttack(state));
};

const smaug = createDragon('Smaug');
smaug.fly();
smaug.attack();`;

    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Architecture</div>
                <h1>Inheritance & Composition</h1>
                <p className="hero-lead">
                    In JavaScript, inheritance is not about copying code; it is about
                    <strong>delegation</strong>. Whether you use deep prototype chains
                    or flat functional composition, mastering how objects share
                    behavior is the key to scalable architecture.
                </p>
            </div>

            <div className="content-section">
                <h2>Classical Prototypal Inheritance</h2>
                <p>
                    Before ES6 classes, inheritance was achieved through
                    <strong>Constructor Stealing</strong> (using <code>call</code>)
                    and <strong>Prototype Linking</strong>. Small mistakes here,
                    like forgetting to reset the <code>constructor</code> property,
                    can lead to broken instance tracking in complex apps.
                </p>
                <CodeBlock language="javascript" code={classInheritanceCode} />
            </div>

            <div className="content-section">
                <h2>The Prototype Chain Mechanics</h2>
                <p>
                    The prototype chain is a linked list of objects. When you access
                    a property, the engine starts at the instance and climbs the
                    <code>[[Prototype]]</code> links until it finds a match or hits
                    <code>null</code>. Understanding this lookup process is vital
                    for optimizing performance in hot code paths.
                </p>
                <CodeBlock language="javascript" code={prototypeChainCode} />
            </div>

            <div className="content-section">
                <h2>Composition: Favoring "Has-A" Over "Is-A"</h2>
                <p>
                    Deep inheritance hierarchies often lead to the
                    "Gorilla/Banana Problem" (you wanted a banana, but got a gorilla
                    holding the banana and the entire jungle). <strong>Composition</strong>
                    allows you to build objects by combining small, specific
                    behaviors rather than rigid taxonomies.
                </p>
                <CodeBlock language="javascript" code={compositionPatternCode} />
                <ProTip><strong> Pro Tip:</strong> Use inheritance (Classes) for
                    UI components where a clear hierarchy exists, but use composition
                    (Mixins/Hooks) for logic and state management to maximize
                    reusability.</ProTip>
            </div>

            <KeyFacts><ul>
                    <li><strong>Delegation:</strong> Prefer prototypes for shared methods to save memory (one function reference for many instances).</li>
                    <li><strong>Flatness:</strong> Keep inheritance chains shallow (2-3 levels max) to avoid performance degradation.</li>
                    <li> <strong>Tight Coupling:</strong> Avoid base classes that "know" too much about their children.</li>
                    <li><strong>Identity:</strong> Always verify <code>instanceof</code> and <code>constructor</code> references after prototype linking.</li>
                    <li><strong>Composition:</strong> Use <code>Object.assign</code> or spread for simple behavior merging.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSInheritance;



